/**
 * ShopItemsDisplay: HUD widget that shows compact icons for all
 * currently owned shop items. Positioned just below the HUD bar,
 * left-aligned. Heart Potions display a "xN" count badge.
 *
 * Icons are small (12x12) with 18px spacing. Only owned items appear.
 * Ring icons emit a colored glow when the player stands on the
 * matching hazard tile (via RING_ACTIVATED / RING_DEACTIVATED events).
 *
 * Durability items show a small arc indicator around their icon:
 *  - Full circle at 100% charges, shrinks clockwise as charges deplete
 *  - Color: white (>50%) → yellow (25-50%) → red (<25%)
 *  - Flicker/pulse warning at <10% charges
 *  - Shatter animation + sound when item breaks (charges = 0)
 */

import Phaser from 'phaser';
import {
  SHOP_ITEM_TEXTURES,
  SHOP_ITEM_NAMES,
  Events,
  ItemCategory,
  SHOP_ITEM_CATEGORIES,
} from '../utils/constants.js';
import {
  BORDER_MARGIN,
  HUD_HEIGHT,
  SHOP_ITEM_LAVA_RING,
  SHOP_ITEM_POISON_RING,
  SHOP_ITEM_WATER_RING,
  SHOP_ITEM_ELEMENTAL_RING,
  COLOR_LAVA_RING,
  COLOR_POISON_RING,
  COLOR_WATER_RING,
  COLOR_ELEMENTAL_RING,
  RING_GLOW_PULSE_SPEED,
  RING_GLOW_MIN_ALPHA,
  RING_GLOW_MAX_ALPHA,
  AUDIO_ITEM_BREAK,
} from '../config/game-config.js';
import { getActiveItemsList } from '../systems/ShopManager.js';
import { getSfxVol } from '../utils/audio-config.js';

/** Size to render each icon (scaled from procedural texture). */
const ICON_SIZE = 12;
/** Horizontal gap between icons. */
const ICON_SPACING = 22;
/** Y position: just below the HUD bar, inside the playable area. */
const STRIP_Y = BORDER_MARGIN + HUD_HEIGHT + 16;
/** X position: left-aligned inside the border. */
const STRIP_X = BORDER_MARGIN + 8;
/** Glow circle radius around each ring icon. */
const GLOW_RADIUS = 8;
/** Radius for the durability arc drawn around icons. */
const ARC_RADIUS = 9;
/** Line width for the durability arc. */
const ARC_LINE_WIDTH = 1.5;
/** Duration (ms) of the low-charge flicker pulse cycle. */
const FLICKER_PULSE_SPEED = 400;
/** Duration (ms) of the shatter animation. */
const SHATTER_DURATION = 500;
/** Number of particles in the shatter animation. */
const SHATTER_PARTICLE_COUNT = 8;
/** Duration (ms) before a click-triggered tooltip auto-hides. */
const TOOLTIP_CLICK_DURATION = 2000;
/** Vertical offset of the tooltip above the icon center. */
const TOOLTIP_OFFSET_Y = -16;

/** Maps ring item IDs to their glow colours. */
const RING_GLOW_COLORS = {
  [SHOP_ITEM_LAVA_RING]: COLOR_LAVA_RING,
  [SHOP_ITEM_POISON_RING]: COLOR_POISON_RING,
  [SHOP_ITEM_WATER_RING]: COLOR_WATER_RING,
  [SHOP_ITEM_ELEMENTAL_RING]: COLOR_ELEMENTAL_RING,
};

/**
 * Get the arc color based on remaining charge percentage.
 * @param {number} ratio - 0..1 (charges / maxCharges)
 * @returns {number} Phaser color integer
 */
function getDurabilityColor(ratio) {
  if (ratio > 0.5) return 0xffffff;  // white
  if (ratio > 0.25) return 0xffcc00; // yellow
  return 0xff3333;                    // red
}

export default class ShopItemsDisplay {
  /**
   * @param {Phaser.Scene} scene - The HUD scene
   * @param {Phaser.Events.EventEmitter} eventBus - Shared event bus
   */
  constructor(scene, eventBus) {
    /** @type {Phaser.Scene} */
    this.scene = scene;
    /** @type {Phaser.Events.EventEmitter} */
    this.eventBus = eventBus;

    /**
     * Per-icon entry: sprite, id, optional durability arc graphics,
     * optional flicker tween, and durability metadata.
     * @type {{ sprite: Phaser.GameObjects.Sprite, id: string,
     *          arcGfx?: Phaser.GameObjects.Graphics,
     *          flickerTween?: Phaser.Tweens.Tween,
     *          durability?: number, maxDurability?: number }[]}
     */
    this._iconEntries = [];
    /** @type {Phaser.GameObjects.Text[]} */
    this._badges = [];

    /** @type {Map<string, { gfx: Phaser.GameObjects.Graphics, tween: Phaser.Tweens.Tween }>} */
    this._activeGlows = new Map();

    /** Active tooltip elements. @private */
    this._tooltip = null;
    /** Timer event for auto-hiding click tooltips. @private */
    this._tooltipTimer = null;

    this._build();
    this._subscribeEvents();
  }

  // ---------------------------------------------------------------
  // Public
  // ---------------------------------------------------------------

  /** Rebuild the icon strip from current shop state. */
  refresh() {
    this._hideTooltip();
    this._clearGlows();
    this._clearIcons();
    this._build();
  }

  /** Clean up all game objects and event listeners. */
  destroy() {
    this._hideTooltip();
    this._unsubscribeEvents();
    this._clearGlows();
    this._clearIcons();
    this.scene = null;
    this.eventBus = null;
  }

  // ---------------------------------------------------------------
  // Private — build / clear
  // ---------------------------------------------------------------

  /** @private Build icons from current owned items. */
  _build() {
    if (!this.scene) return;

    const items = getActiveItemsList();
    let x = STRIP_X;

    for (const entry of items) {
      const textureKey = SHOP_ITEM_TEXTURES[entry.id];
      if (!textureKey) continue;

      // Icon sprite
      const icon = this.scene.add.sprite(x, STRIP_Y, textureKey);
      icon.setDisplaySize(ICON_SIZE, ICON_SIZE);
      icon.setOrigin(0, 0.5);
      icon.setDepth(210);
      icon.setAlpha(0.85);

      // Make icon interactive for tooltip
      icon.setInteractive({ useHandCursor: true });

      const iconEntry = {
        sprite: icon,
        id: entry.id,
        arcGfx: null,
        flickerTween: null,
        durability: entry.durability ?? 0,
        maxDurability: entry.maxDurability ?? 0,
      };

      // Hover: show tooltip while pointer is over icon
      icon.on('pointerover', () => {
        this._showTooltip(iconEntry, false);
      });
      icon.on('pointerout', () => {
        // Only hide if this is a hover tooltip (not a click-locked one)
        if (this._tooltip && !this._tooltip.clickLocked) {
          this._hideTooltip();
        }
      });
      // Click: show tooltip temporarily
      icon.on('pointerdown', () => {
        this._showTooltip(iconEntry, true);
      });

      // Durability arc for durability-category items
      const category = SHOP_ITEM_CATEGORIES[entry.id];
      if (category === ItemCategory.DURABILITY && entry.maxDurability > 0) {
        const arcGfx = this.scene.add.graphics();
        arcGfx.setDepth(209);
        iconEntry.arcGfx = arcGfx;
        this._drawDurabilityArc(iconEntry, x);

        // Start flicker if charges < 10%
        const ratio = entry.durability / entry.maxDurability;
        if (ratio > 0 && ratio < 0.1) {
          this._startFlicker(iconEntry);
        }
      }

      this._iconEntries.push(iconEntry);

      // Count badge for stackable items (Heart Potion x2, x3, etc.)
      if (entry.count > 1) {
        const badge = this.scene.add.text(
          x + ICON_SIZE + 1,
          STRIP_Y + 2,
          `x${entry.count}`,
          {
            fontFamily: 'monospace',
            fontSize: '8px',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 2,
          },
        );
        badge.setOrigin(0, 0.5);
        badge.setDepth(210);
        this._badges.push(badge);

        x += ICON_SIZE + badge.width + 6;
      } else {
        x += ICON_SPACING;
      }
    }
  }

  /** @private Remove all icon sprites, arcs, flicker tweens, and badges. */
  _clearIcons() {
    for (const entry of this._iconEntries) {
      if (entry.flickerTween) entry.flickerTween.stop();
      if (entry.arcGfx) entry.arcGfx.destroy();
      if (entry.sprite) entry.sprite.destroy();
    }
    this._iconEntries = [];

    for (const badge of this._badges) {
      if (badge) badge.destroy();
    }
    this._badges = [];
  }

  // ---------------------------------------------------------------
  // Private — durability arc
  // ---------------------------------------------------------------

  /**
   * Draw the durability arc around an icon.
   * Full circle = 100%, arc shrinks clockwise as charges deplete.
   * @private
   * @param {Object} iconEntry - Icon entry with arcGfx, durability, maxDurability
   * @param {number} x - The x origin of the icon
   */
  _drawDurabilityArc(iconEntry, x) {
    const { arcGfx, durability, maxDurability } = iconEntry;
    if (!arcGfx || maxDurability <= 0) return;

    arcGfx.clear();

    const ratio = durability / maxDurability;
    if (ratio <= 0) return;

    const color = getDurabilityColor(ratio);
    const alpha = 0.7;

    // Arc center: icon center
    const cx = x + ICON_SIZE / 2;
    const cy = STRIP_Y;

    // Start from top (- PI/2), draw clockwise. Full = 2*PI.
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + (2 * Math.PI * ratio);

    arcGfx.lineStyle(ARC_LINE_WIDTH, color, alpha);
    arcGfx.beginPath();
    arcGfx.arc(cx, cy, ARC_RADIUS, startAngle, endAngle, false);
    arcGfx.strokePath();
  }

  /**
   * Update the durability arc for a specific item after a durability change.
   * @private
   * @param {string} itemId
   * @param {number} charges - Current charges remaining
   * @param {number} maxCharges - Maximum charges
   */
  _updateDurabilityArc(itemId, charges, maxCharges) {
    const entry = this._iconEntries.find((e) => e.id === itemId);
    if (!entry) return;

    entry.durability = charges;
    // Keep existing maxDurability if maxCharges not provided by emitter
    if (maxCharges != null) entry.maxDurability = maxCharges;

    if (entry.arcGfx && entry.maxDurability > 0) {
      // Recalculate x from sprite position
      const x = entry.sprite.x;
      this._drawDurabilityArc(entry, x);

      // Manage flicker based on charge ratio
      const ratio = charges / entry.maxDurability;
      if (ratio > 0 && ratio < 0.1 && !entry.flickerTween) {
        this._startFlicker(entry);
      } else if (ratio >= 0.1 && entry.flickerTween) {
        this._stopFlicker(entry);
      }
    }
  }

  /**
   * Start a flicker/pulse effect on an icon when charges are critically low (<10%).
   * @private
   * @param {Object} iconEntry
   */
  _startFlicker(iconEntry) {
    if (!this.scene || iconEntry.flickerTween) return;

    const proxy = { alpha: 0.85 };
    iconEntry.flickerTween = this.scene.tweens.add({
      targets: proxy,
      alpha: 0.3,
      duration: FLICKER_PULSE_SPEED,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        if (iconEntry.sprite) iconEntry.sprite.setAlpha(proxy.alpha);
        if (iconEntry.arcGfx) iconEntry.arcGfx.setAlpha(proxy.alpha);
      },
    });
  }

  /**
   * Stop flicker effect and restore normal alpha.
   * @private
   * @param {Object} iconEntry
   */
  _stopFlicker(iconEntry) {
    if (iconEntry.flickerTween) {
      iconEntry.flickerTween.stop();
      iconEntry.flickerTween = null;
    }
    if (iconEntry.sprite) iconEntry.sprite.setAlpha(0.85);
    if (iconEntry.arcGfx) iconEntry.arcGfx.setAlpha(1);
  }

  // ---------------------------------------------------------------
  // Private — shatter animation
  // ---------------------------------------------------------------

  /**
   * Play a shatter/break animation for an item that has run out of charges.
   * The icon splits into small particles that fly outward and fade.
   * @private
   * @param {string} itemId
   */
  _playShatterAnimation(itemId) {
    if (!this.scene) return;

    const entry = this._iconEntries.find((e) => e.id === itemId);
    if (!entry || !entry.sprite) return;

    // Stop flicker if active
    this._stopFlicker(entry);

    const cx = entry.sprite.x + ICON_SIZE / 2;
    const cy = entry.sprite.y;

    // Hide original icon and arc immediately
    entry.sprite.setVisible(false);
    if (entry.arcGfx) entry.arcGfx.setVisible(false);

    // Play break sound
    try {
      this.scene.sound.play(AUDIO_ITEM_BREAK, { volume: getSfxVol(0.5) });
    } catch { /* noop */ }

    // Create small particle fragments
    const particles = [];
    for (let i = 0; i < SHATTER_PARTICLE_COUNT; i++) {
      const angle = (i / SHATTER_PARTICLE_COUNT) * Math.PI * 2;
      const size = 1 + Math.random() * 2;

      const frag = this.scene.add.graphics();
      frag.setDepth(211);
      frag.fillStyle(0xffffff, 0.9);
      frag.fillRect(-size / 2, -size / 2, size, size);
      frag.setPosition(cx, cy);

      const dist = 12 + Math.random() * 10;
      const targetX = cx + Math.cos(angle) * dist;
      const targetY = cy + Math.sin(angle) * dist;

      this.scene.tweens.add({
        targets: frag,
        x: targetX,
        y: targetY,
        alpha: 0,
        duration: SHATTER_DURATION,
        ease: 'Quad.easeOut',
        onComplete: () => {
          frag.destroy();
        },
      });

      particles.push(frag);
    }

    // Brief white flash at icon center
    const flash = this.scene.add.graphics();
    flash.setDepth(211);
    flash.fillStyle(0xffffff, 0.8);
    flash.fillCircle(cx, cy, ICON_SIZE / 2 + 2);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: SHATTER_DURATION * 0.5,
      ease: 'Quad.easeOut',
      onComplete: () => {
        flash.destroy();
        // Rebuild the strip after shatter completes
        this.refresh();
      },
    });
  }

  // ---------------------------------------------------------------
  // Private — tooltip
  // ---------------------------------------------------------------

  /**
   * Show a tooltip above the given icon entry.
   * @private
   * @param {Object} iconEntry - The icon entry to show tooltip for
   * @param {boolean} clickLocked - If true, auto-dismiss after TOOLTIP_CLICK_DURATION
   */
  _showTooltip(iconEntry, clickLocked) {
    if (!this.scene) return;

    // Remove any existing tooltip first
    this._hideTooltip();

    const name = SHOP_ITEM_NAMES[iconEntry.id] || iconEntry.id;
    const category = SHOP_ITEM_CATEGORIES[iconEntry.id];
    const hasDurability = category === ItemCategory.DURABILITY
      && iconEntry.maxDurability > 0;

    let label = name;
    if (hasDurability) {
      label += ` (${iconEntry.durability}/${iconEntry.maxDurability})`;
    }

    const cx = iconEntry.sprite.x + ICON_SIZE / 2;
    const cy = STRIP_Y + TOOLTIP_OFFSET_Y;

    // Background
    const text = this.scene.add.text(cx, cy, label, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '9px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5, 1).setDepth(220);

    // Slight padding background behind text for readability
    const pad = 3;
    const bg = this.scene.add.graphics().setDepth(219);
    bg.fillStyle(0x000000, 0.75);
    bg.fillRoundedRect(
      text.x - text.width / 2 - pad,
      text.y - text.height - pad,
      text.width + pad * 2,
      text.height + pad * 2,
      3,
    );

    this._tooltip = { text, bg, clickLocked };

    if (clickLocked) {
      this._tooltipTimer = this.scene.time.delayedCall(
        TOOLTIP_CLICK_DURATION,
        () => { this._hideTooltip(); },
      );
    }
  }

  /**
   * Hide and destroy the active tooltip.
   * @private
   */
  _hideTooltip() {
    if (this._tooltipTimer) {
      this._tooltipTimer.destroy();
      this._tooltipTimer = null;
    }
    if (this._tooltip) {
      if (this._tooltip.text) this._tooltip.text.destroy();
      if (this._tooltip.bg) this._tooltip.bg.destroy();
      this._tooltip = null;
    }
  }

  // ---------------------------------------------------------------
  // Private — ring glow effects
  // ---------------------------------------------------------------

  /**
   * Start a pulsing glow effect on the ring icon for the given item ID.
   * @private
   * @param {string} itemId
   */
  _startRingGlow(itemId) {
    if (!this.scene || this._activeGlows.has(itemId)) return;

    const color = RING_GLOW_COLORS[itemId];
    if (color === undefined) return;

    // Find the matching icon
    const entry = this._iconEntries.find((e) => e.id === itemId);
    if (!entry) return;

    const cx = entry.sprite.x + ICON_SIZE / 2;
    const cy = entry.sprite.y;

    const gfx = this.scene.add.graphics();
    gfx.setDepth(209); // just behind the icon

    // Initial draw
    gfx.clear();
    gfx.fillStyle(color, RING_GLOW_MIN_ALPHA);
    gfx.fillCircle(cx, cy, GLOW_RADIUS);

    // Pulsing tween — animate a proxy value and redraw on each update
    const proxy = { alpha: RING_GLOW_MIN_ALPHA };
    const tween = this.scene.tweens.add({
      targets: proxy,
      alpha: RING_GLOW_MAX_ALPHA,
      duration: RING_GLOW_PULSE_SPEED,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        gfx.clear();
        gfx.fillStyle(color, proxy.alpha);
        gfx.fillCircle(cx, cy, GLOW_RADIUS);
      },
    });

    this._activeGlows.set(itemId, { gfx, tween });
  }

  /**
   * Stop the pulsing glow on the ring icon for the given item ID.
   * @private
   * @param {string} itemId
   */
  _stopRingGlow(itemId) {
    const glowData = this._activeGlows.get(itemId);
    if (!glowData) return;

    if (glowData.tween) glowData.tween.stop();
    if (glowData.gfx) glowData.gfx.destroy();
    this._activeGlows.delete(itemId);
  }

  /** @private Remove all active glow effects. */
  _clearGlows() {
    for (const [, glowData] of this._activeGlows) {
      if (glowData.tween) glowData.tween.stop();
      if (glowData.gfx) glowData.gfx.destroy();
    }
    this._activeGlows.clear();
  }

  // ---------------------------------------------------------------
  // Private — event handling
  // ---------------------------------------------------------------

  /** @private */
  _subscribeEvents() {
    if (!this.eventBus) return;

    this._onRingActivated = (itemId) => this._startRingGlow(itemId);
    this._onRingDeactivated = (itemId) => this._stopRingGlow(itemId);
    this._onDurabilityChanged = (itemId, charges, maxCharges) =>
      this._updateDurabilityArc(itemId, charges, maxCharges);
    this._onItemBroken = (itemId) => this._playShatterAnimation(itemId);
    this._onShopItemsChanged = () => this.refresh();

    this.eventBus.on(Events.RING_ACTIVATED, this._onRingActivated);
    this.eventBus.on(Events.RING_DEACTIVATED, this._onRingDeactivated);
    this.eventBus.on(Events.ITEM_DURABILITY_CHANGED, this._onDurabilityChanged);
    this.eventBus.on(Events.ITEM_BROKEN, this._onItemBroken);
    this.eventBus.on(Events.SHOP_ITEMS_CHANGED, this._onShopItemsChanged);
  }

  /** @private */
  _unsubscribeEvents() {
    if (!this.eventBus) return;

    if (this._onRingActivated) {
      this.eventBus.off(Events.RING_ACTIVATED, this._onRingActivated);
    }
    if (this._onRingDeactivated) {
      this.eventBus.off(Events.RING_DEACTIVATED, this._onRingDeactivated);
    }
    if (this._onDurabilityChanged) {
      this.eventBus.off(Events.ITEM_DURABILITY_CHANGED, this._onDurabilityChanged);
    }
    if (this._onItemBroken) {
      this.eventBus.off(Events.ITEM_BROKEN, this._onItemBroken);
    }
    if (this._onShopItemsChanged) {
      this.eventBus.off(Events.SHOP_ITEMS_CHANGED, this._onShopItemsChanged);
    }
  }
}
